import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationCenterService, NotificationFilters } from '../../core/services/notification-center.service';
import { NotificationCenter, NotificationType, NotificationPriority } from '../../core/interfaces/notification';
import { EventService } from '../../core/services/event.service';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-notification-center',
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
  standalone: false
})
export class NotificationCenterComponent implements OnInit {
  private readonly notificationService = inject(NotificationCenterService);
  private readonly eventService = inject(EventService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  notifications$ = this.notificationService.notifications$;
  loading$ = this.notificationService.loading$;
  error$ = this.notificationService.error$;
  state$ = this.notificationService.state$;

  readonly NotificationType = NotificationType;
  readonly NotificationPriority = NotificationPriority;

  activeTab: 'active' | 'archived' = 'active';
  selectedType: NotificationType | 'all' = 'all';
  showOnlyUnread = false;
  typeOptions: { value: string; label: string }[] = [];

  ngOnInit(): void {
    this.buildTypeOptions();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildTypeOptions();
        this.notificationService.reloadForLocaleChange();
      });
    this.syncLoadingWithGlobalLoader();
    this.activeTab = 'active';
    this.selectedType = 'all';
    this.showOnlyUnread = false;
    this.notificationService.resetToDefaults();
  }

  private syncLoadingWithGlobalLoader(): void {
    this.loading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isLoading => {
      if (isLoading) {
        this.eventService.broadcast('globalLoader:show', null);
      } else {
        this.eventService.broadcast('globalLoader:hide', null);
      }
    });
  }

  private buildTypeOptions(): void {
    this.typeOptions = [
      { value: 'all', label: this.translate.instant('HEADER.NOTIFICATIONS.ALL_TYPES') },
      { value: 'booking', label: this.translate.instant('HEADER.NOTIFICATIONS.TYPE_BOOKING') },
      { value: 'security', label: this.translate.instant('HEADER.NOTIFICATIONS.TYPE_SECURITY') },
      { value: 'hotel_info', label: this.translate.instant('HEADER.NOTIFICATIONS.TYPE_HOTEL_INFO') },
      { value: 'hotel_status', label: this.translate.instant('HEADER.NOTIFICATIONS.TYPE_HOTEL_STATUS') },
      { value: 'hotel_settings', label: this.translate.instant('HEADER.NOTIFICATIONS.TYPE_HOTEL_SETTINGS') },
      { value: 'system', label: this.translate.instant('HEADER.NOTIFICATIONS.TYPE_SYSTEM') }
    ];
  }

  loadMore(): void {
    this.notificationService.loadMore();
  }

  get isArchivedTab(): boolean {
    return this.activeTab === 'archived';
  }

  switchTab(tab: 'active' | 'archived'): void {
    this.activeTab = tab;
    this.notificationService.switchTab(tab);
  }

  onFilterChange(): void {
    const filters: NotificationFilters = {};

    if (this.selectedType !== 'all') {
      filters.type = this.selectedType;
    }

    if (this.showOnlyUnread) {
      filters.isRead = false;
    }

    this.notificationService.applyFilters(filters);
  }

  markAsRead(notification: NotificationCenter): void {
    if (!notification.isRead) {
      this.notificationService.markAsRead(notification.uuid);
    }
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead();
  }

  archiveNotification(notification: NotificationCenter, event: Event): void {
    event.stopPropagation();
    this.notificationService.archiveNotification(notification.uuid);
  }

  deleteNotification(notification: NotificationCenter, event: Event): void {
    event.stopPropagation();
    this.notificationService.deleteNotification(notification.uuid);
  }

  handleNotificationClick(notification: NotificationCenter): void {
    this.markAsRead(notification);
    this.notificationService.navigateToNotification(notification);
  }

  getNotificationIcon(notification: NotificationCenter): string {
    switch (notification.type) {
      case NotificationType.BOOKING:
        return 'bed-double';
      case NotificationType.SECURITY:
        return 'shield-alert';
      case NotificationType.HOTEL_INFO:
        return 'info';
      case NotificationType.HOTEL_STATUS:
        return 'building-2';
      case NotificationType.HOTEL_SETTINGS:
        return 'settings';
      case NotificationType.SYSTEM:
        return 'bell';
      default:
        return 'bell';
    }
  }

  getNotificationColor(notification: NotificationCenter): string {
    if (notification.priority === NotificationPriority.URGENT) {
      return 'text-danger';
    }

    switch (notification.type) {
      case NotificationType.BOOKING:
        return 'text-primary';
      case NotificationType.SECURITY:
        return 'text-danger';
      case NotificationType.HOTEL_INFO:
        return 'text-info';
      case NotificationType.HOTEL_STATUS:
        return 'text-warning';
      case NotificationType.HOTEL_SETTINGS:
        return 'text-secondary';
      case NotificationType.SYSTEM:
        return 'text-muted';
      default:
        return 'text-muted';
    }
  }

  getPriorityBadgeColor(priority: NotificationPriority): 'primary' | 'secondary' | 'neutral' | 'success' | 'warning' {
    switch (priority) {
      case NotificationPriority.URGENT:
        return 'primary';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.MEDIUM:
        return 'secondary';
      case NotificationPriority.LOW:
        return 'neutral';
      default:
        return 'neutral';
    }
  }

  clearError(): void {
    this.notificationService.clearError();
  }

  getTimeAgo(createdAt: string): string {
    return DateTime.fromISO(createdAt)
      .setLocale(this.translate.currentLang)
      .toRelative() ?? '';
  }

  getVisibleNotifications(notifications: NotificationCenter[]): NotificationCenter[] {
    if (!this.showOnlyUnread) {
      return notifications;
    }
    return notifications.filter(notification => !notification.isRead);
  }

  trackByNotificationId(_index: number, notification: NotificationCenter): string {
    return notification.uuid;
  }
}
